// Time zone and calendar-day helpers for the demo booking calendar. Days are
// "YYYY-MM-DD" keys in the visitor's chosen zone; times come from the API as
// UTC instants and are only ever formatted in that zone.

const pad = (n) => String(n).padStart(2, "0");
const formatters = new Map();

function wallFormatter(tz) {
  if (!formatters.has(tz)) {
    formatters.set(
      tz,
      new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        hourCycle: "h23",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }),
    );
  }
  return formatters.get(tz);
}

export function isValidZone(tz) {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return !!tz;
  } catch {
    return false;
  }
}

export function detectTimeZone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidZone(tz) ? tz : "America/Chicago";
  } catch {
    return "America/Chicago";
  }
}

/** 12-hour clock unless the visitor's locale uses a 24-hour one. */
export function prefers12h() {
  try {
    const cycle = new Intl.DateTimeFormat(undefined, { hour: "numeric" }).resolvedOptions().hourCycle;
    return !cycle || cycle === "h12" || cycle === "h11";
  } catch {
    return true;
  }
}

/** { y, mo, d, h, mi } on the wall clock in `tz`. */
export function wallClock(ms, tz) {
  const parts = {};
  for (const p of wallFormatter(tz).formatToParts(ms)) parts[p.type] = p.value;
  return { y: +parts.year, mo: +parts.month, d: +parts.day, h: +parts.hour % 24, mi: +parts.minute };
}

/** Minutes `tz` is ahead of UTC at `ms`. */
export function offsetMinutes(ms, tz) {
  const w = wallClock(ms, tz);
  const minute = Math.floor(ms / 60000) * 60000;
  return Math.round((Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi) - minute) / 60000);
}

/** The instant a wall-clock time in `tz` happens. */
export function zonedToUtc({ y, mo, d, h = 0, mi = 0 }, tz) {
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  const first = guess - offsetMinutes(guess, tz) * 60000;
  return guess - offsetMinutes(first, tz) * 60000;
}

export function dayKey(ms, tz) {
  const w = wallClock(ms, tz);
  return `${w.y}-${pad(w.mo)}-${pad(w.d)}`;
}

export function keyParts(key) {
  const [y, mo, d] = String(key).split("-").map(Number);
  return { y, mo, d };
}

export function addDays(key, n) {
  const { y, mo, d } = keyParts(key);
  const t = new Date(Date.UTC(y, mo - 1, d + n));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

/** 0 (Sunday) to 6. */
export function weekday(key) {
  const { y, mo, d } = keyParts(key);
  return new Date(Date.UTC(y, mo - 1, d)).getUTCDay();
}

/** A day key formatted with Intl options, e.g. { weekday: "short" }. */
export function formatDay(key, options) {
  const { y, mo, d } = keyParts(key);
  return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", ...options }).format(Date.UTC(y, mo - 1, d, 12));
}

/** "9:30am" or "09:30". */
export function formatTime(ms, tz, hour12) {
  if (!hour12) {
    return new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", hour: "2-digit", minute: "2-digit" }).format(ms);
  }
  const text = new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit", hour12: true }).format(ms);
  return text.replace(/\s*([AP])M$/i, (_, p) => `${p.toLowerCase()}m`);
}

/** "GMT-6", "GMT+5:30", "GMT". */
export function gmtLabel(tz, ms = Date.now()) {
  const off = offsetMinutes(ms, tz);
  if (!off) return "GMT";
  const sign = off < 0 ? "-" : "+";
  const abs = Math.abs(off);
  return `GMT${sign}${Math.floor(abs / 60)}${abs % 60 ? `:${pad(abs % 60)}` : ""}`;
}

/** The zone's long name, e.g. "Central Standard Time". */
export function zoneLongName(tz, ms = Date.now()) {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "long" })
      .formatToParts(ms)
      .find((p) => p.type === "timeZoneName");
    return part?.value || tz;
  } catch {
    return tz;
  }
}

const COMMON_ZONES = [
  ["Pacific/Honolulu", "Hawaii"],
  ["America/Anchorage", "Alaska"],
  ["America/Los_Angeles", "Pacific Time (US & Canada)"],
  ["America/Phoenix", "Arizona"],
  ["America/Denver", "Mountain Time (US & Canada)"],
  ["America/Chicago", "Central Time (US & Canada)"],
  ["America/Mexico_City", "Mexico City"],
  ["America/New_York", "Eastern Time (US & Canada)"],
  ["America/Bogota", "Bogotá, Lima"],
  ["America/Halifax", "Atlantic Time (Canada)"],
  ["America/St_Johns", "Newfoundland"],
  ["America/Sao_Paulo", "São Paulo"],
  ["America/Argentina/Buenos_Aires", "Buenos Aires"],
  ["UTC", "UTC"],
  ["Europe/London", "London, Dublin, Lisbon"],
  ["Africa/Lagos", "Lagos"],
  ["Europe/Berlin", "Berlin, Paris, Madrid, Rome"],
  ["Africa/Johannesburg", "Johannesburg"],
  ["Europe/Athens", "Athens, Helsinki, Kyiv"],
  ["Africa/Cairo", "Cairo"],
  ["Africa/Nairobi", "Nairobi"],
  ["Europe/Istanbul", "Istanbul"],
  ["Europe/Moscow", "Moscow"],
  ["Asia/Dubai", "Dubai"],
  ["Asia/Karachi", "Karachi"],
  ["Asia/Kolkata", "India"],
  ["Asia/Dhaka", "Dhaka"],
  ["Asia/Bangkok", "Bangkok, Jakarta"],
  ["Asia/Singapore", "Singapore, Kuala Lumpur"],
  ["Asia/Shanghai", "Beijing, Shanghai"],
  ["Asia/Hong_Kong", "Hong Kong"],
  ["Asia/Manila", "Manila"],
  ["Asia/Tokyo", "Tokyo"],
  ["Asia/Seoul", "Seoul"],
  ["Australia/Perth", "Perth"],
  ["Australia/Adelaide", "Adelaide"],
  ["Australia/Brisbane", "Brisbane"],
  ["Australia/Sydney", "Sydney, Melbourne"],
  ["Pacific/Auckland", "Auckland"],
];

/** [{ value, label }] sorted by offset; `include` is added when missing. */
export function zoneOptions(include, ms = Date.now()) {
  const list = COMMON_ZONES.filter(([tz]) => isValidZone(tz));
  if (include && isValidZone(include) && !list.some(([tz]) => tz === include)) {
    list.push([include, include.split("/").pop().replace(/_/g, " ")]);
  }
  return list
    .map(([value, name]) => ({ value, name, offset: offsetMinutes(ms, value) }))
    .sort((a, b) => a.offset - b.offset || a.name.localeCompare(b.name))
    .map(({ value, name }) => ({ value, label: `(${gmtLabel(value, ms)}) ${name}` }));
}
